import moment from 'moment';
import {Moment} from 'moment';
import React from 'react';

import {
  CalendarInteraction,
  CalendarItem as CalendarItemType,
  LayeredCalendarItem,
  LayeredCalendarItems,
} from 'calendar/CalendarConstants';

import CalendarItem, {CalendarItemProps} from './CalendarItem';
import CalendarUtil from './CalendarUtil';
import HeaderDate from './HeaderDate';
import MouseDownCapturer from './MouseDownCapturer';
import MouseOverCapturer from './MouseOverCapturer';

const MIN_LAYERS = 3;
const DAYS_IN_WEEK = 7;

interface Props {
  items: LayeredCalendarItems<CalendarItemType>;
  renderDate: (date: Moment) => JSX.Element;
  weekStart: moment.Moment;
  interact: CalendarInteraction;
  today: moment.Moment;
}

class WeekComponent extends React.Component<Props> {
  getDays = (): Array<Moment> =>
    [0, 1, 2, 3, 4, 5, 6].map((days) => this.props.weekStart.clone().add(days, 'days'));

  dateFromDayNum = (dayNum: number): Moment => this.props.weekStart.clone().add(dayNum, 'days');

  _calendarItem = (itemProps: CalendarItemProps & {key: string}): JSX.Element => {
    if (itemProps.item != null) {
      return <CalendarItem {...itemProps} key={itemProps.item.id} />;
    } else {
      return (
        <MouseDownCapturer key={itemProps.key} date={itemProps.date}>
          {(onMouseDown) => (
            <MouseOverCapturer date={itemProps.date}>
              {(onMouseOver) => (
                <CalendarItem {...itemProps} onMouseDown={onMouseDown} onMouseOver={onMouseOver} />
              )}
            </MouseOverCapturer>
          )}
        </MouseDownCapturer>
      );
    }
  };

  renderCellsFromLayer = (layer: Array<LayeredCalendarItem<CalendarItemType>>): Array<JSX.Element> => {
    let layerIdx = 0;
    const cells = [];
    let dayNum = 0;
    // Assuming layer is sorted array of items (by start date), we iterate through the days and render the
    // items as we find them.
    while (dayNum < DAYS_IN_WEEK) {
      const date = this.dateFromDayNum(dayNum);
      const item = layer[layerIdx];
      const key = date.format();
      // We're past the end of items.
      if (item == null) {
        cells.push(this._calendarItem({key, date}));
        dayNum++;
        continue;
      }
      // Next item is in the future still.
      if (CalendarUtil.getItemDate(item).isAfter(date)) {
        cells.push(this._calendarItem({key, date}));
        dayNum++;
        continue;
      }
      // We've found an item starting on this date, render it.
      let {numDays} = item;
      // Account for items starting before the week.
      const elapsedDays = date.diff(CalendarUtil.getItemDate(item), 'days');
      numDays -= elapsedDays;
      // Account for items extending past the week.
      const daysLeftInWeek = DAYS_IN_WEEK - dayNum;
      numDays = Math.min(numDays, daysLeftInWeek);
      cells.push(this._calendarItem({key, date, item, numDays, interact: this.props.interact}));
      layerIdx++;
      // Forward the day we're rendering, especially if the item spanned multiple days.
      dayNum += numDays;
    }
    return cells;
  };

  renderEmptyLayer = (key: number): JSX.Element => (
    <tr key={key}>
      {[0, 1, 2, 3, 4, 5, 6].map((dayNum) => {
        const date = this.dateFromDayNum(dayNum);
        return this._calendarItem({key: date.format(), date});
      })}
    </tr>
  );

  override render(): JSX.Element {
    let numLayers = 0;
    return (
      <div className="weekComponent">
        <table className="calendarTable">
          <thead>
            <tr>
              {this.getDays().map((date) => (
                <MouseDownCapturer key={date.format()} date={date}>
                  {(onMouseDown) => (
                    <MouseOverCapturer date={date}>
                      {(onMouseOver) => (
                        <HeaderDate
                          date={date}
                          onMouseDown={onMouseDown}
                          onMouseOver={onMouseOver}
                          today={this.props.today}
                          interact={this.props.interact}
                          renderDate={this.props.renderDate}
                        />
                      )}
                    </MouseOverCapturer>
                  )}
                </MouseDownCapturer>
              ))}
            </tr>
          </thead>
          <tbody>
            {
              // Pad to at least MIN_LAYERS.
              (() => {
                const result = [];
                for (let idx = 0; idx < this.props.items.length; idx++) {
                  // Skip empty layers.
                  const layer = this.props.items[idx];
                  if (layer.length === 0) {
                    continue;
                  }
                  numLayers++;
                  result.push(<tr key={idx}>{this.renderCellsFromLayer(layer)}</tr>);
                }
                return result;
              })()
            }
            {(() => {
              const result1 = [];
              while (numLayers < MIN_LAYERS) {
                numLayers++;
                result1.push(this.renderEmptyLayer(numLayers));
              }
              return result1;
            })()}
          </tbody>
        </table>
      </div>
    );
  }
}

export default WeekComponent;
